/**
 * Exit P/E Context Chart for Valuation Scorecard.
 *
 * Renders a compact Highcharts chart showing the historical Trailing PE as a
 * light area backdrop, with the analyst's Exit P/E range (High→Low) as a
 * shaded plotBand and prominent plotlines for Exit Base, Current PE, and
 * the historical median. Designed for quick visual context — "Where do the
 * analyst's exit PE assumptions sit relative to the stock's historical PE?"
 */

import Highcharts from "highcharts/highstock";
import { isDarkMode, setupThemeObserver } from "../../common/chartTheme";
import { CHART_EXPORTING_CONFIG, DEFAULT_VALUE_DECIMALS } from "../../company_detail/constants";
import type { ValuationChartResponse } from "../../company_detail/interfaces";
import { createChartLifecycle } from "./chartLifecycle";

/** Theme-aware colors for the chart. */
interface ExitPeThemeColors {
  areaLine: string;
  areaFillFrom: string;
  exitBase: string;
  bandFill: string;
  bandLabel: string;
  currentPe: string;
  median: string;
  labelColor: string;
}

let themeObserver: MutationObserver | null = null;
let nextRequestId = 0;

interface InFlightRequest {
  requestId: number;
  container: HTMLElement;
  controller: AbortController;
  signature: string;
}

const inFlightRequests: Map<string, InFlightRequest> = new Map();

interface RenderedChartState {
  container: HTMLElement;
  signature: string;
}

const renderedChartStates: Map<string, RenderedChartState> = new Map();

const RANGE_SELECTOR_BUTTONS: Highcharts.RangeSelectorButtonsOptions[] = [
  { type: "year", count: 1, text: "1y" },
  { type: "year", count: 3, text: "3y" },
  { type: "year", count: 5, text: "5y" },
  { type: "year", count: 10, text: "10y" },
  { type: "all", text: "All" },
];

function getThemeColors(): ExitPeThemeColors {
  const dark = isDarkMode();
  return {
    areaLine: dark ? "#60a5fa" : "#3b82f6",
    areaFillFrom: dark ? "rgba(96,165,250,0.15)" : "rgba(59,130,246,0.12)",
    exitBase: dark ? "#fbbf24" : "#f59e0b",
    bandFill: dark ? "rgba(251,191,36,0.10)" : "rgba(245,158,11,0.08)",
    bandLabel: dark ? "rgba(251,191,36,0.60)" : "rgba(245,158,11,0.50)",
    currentPe: dark ? "#9ca3af" : "#6b7280",
    median: dark ? "#6b7280" : "#9ca3af",
    labelColor: dark ? "#d1d5db" : "#4b5563",
  };
}

function normalizeDataValue(rawValue: string | undefined): string {
  const normalizedValue = (rawValue ?? "").trim();
  if (!normalizedValue || normalizedValue === "None") {
    return "";
  }
  return normalizedValue;
}

function getCompanyId(container: HTMLElement): string | null {
  const companyId = normalizeDataValue(container.dataset.companyId);
  return companyId || null;
}

function getRenderSignature(container: HTMLElement): string {
  const theme = isDarkMode() ? "dark" : "light";

  return [
    getCompanyId(container) ?? "",
    normalizeDataValue(container.dataset.currencyCode),
    normalizeDataValue(container.dataset.exitPeHigh),
    normalizeDataValue(container.dataset.exitPeBase),
    normalizeDataValue(container.dataset.exitPeLow),
    normalizeDataValue(container.dataset.currentPe),
    theme,
  ].join("|");
}

/** Parses a numeric data-attribute from the container, returning null for missing/invalid values. */
function parseDataAttr(container: HTMLElement, attr: string): number | null {
  const raw = container.dataset[attr];
  if (!raw || raw === "" || raw === "None") return null;
  const value = Number.parseFloat(raw);
  return Number.isNaN(value) ? null : value;
}

/** Computes the median of the y-values in a [timestamp, value] array. */
function computeMedian(data: Array<[number, number]>): number | null {
  const values = data.map((d) => d[1]).filter((v) => !Number.isNaN(v) && Number.isFinite(v));
  if (values.length === 0) return null;
  values.sort((a, b) => a - b);
  const mid = Math.floor(values.length / 2);
  return values.length % 2 ? values[mid] : (values[mid - 1] + values[mid]) / 2;
}

/** Shows an error message inside the chart container. */
function showChartError(container: HTMLElement, message: string): void {
  const wrapper = document.createElement("div");
  wrapper.className = "flex items-center justify-center h-full";

  const inner = document.createElement("div");
  inner.className = "text-center";

  const p = document.createElement("p");
  p.className = "text-gray-500 dark:text-gray-400";
  p.textContent = message;

  inner.appendChild(p);
  wrapper.appendChild(inner);
  container.replaceChildren(wrapper);
}

/** Transforms raw valuation data for a field into Highcharts [timestamp, value] tuples. */
function transformData<T extends { pricing_date: string }>(
  data: T[],
  field: keyof T,
): Array<[number, number]> {
  return data.map((point) => [
    new Date(point.pricing_date).getTime(),
    Number.parseFloat(String(point[field])),
  ]);
}

function cancelInFlightRequest(containerId: string): void {
  const inFlight = inFlightRequests.get(containerId);
  if (!inFlight) return;
  inFlight.controller.abort();
  inFlightRequests.delete(containerId);
}

function isLatestRequest(container: HTMLElement, requestId: number): boolean {
  const inFlight = inFlightRequests.get(container.id);
  return (
    !!inFlight &&
    inFlight.requestId === requestId &&
    inFlight.container === container &&
    document.getElementById(container.id) === container
  );
}

function hasMatchingRenderedChart(container: HTMLElement, signature: string): boolean {
  const renderedState = renderedChartStates.get(container.id);
  if (!renderedState) return false;
  return (
    renderedState.container === container &&
    renderedState.signature === signature &&
    lifecycle.instances.has(container.id)
  );
}

function hasMatchingInFlightRequest(container: HTMLElement, signature: string): boolean {
  const inFlight = inFlightRequests.get(container.id);
  if (!inFlight) return false;
  return inFlight.container === container && inFlight.signature === signature;
}

function renderChartForContainer(container: HTMLElement): void {
  if (!container.id) return;

  const companyId = getCompanyId(container);
  if (!companyId) {
    cancelInFlightRequest(container.id);
    lifecycle.destroyChart(container.id);
    renderedChartStates.delete(container.id);
    return;
  }

  if (!lifecycle.isContainerVisible(container)) {
    cancelInFlightRequest(container.id);
    lifecycle.destroyChart(container.id);
    renderedChartStates.delete(container.id);
    return;
  }

  const signature = getRenderSignature(container);
  if (hasMatchingRenderedChart(container, signature)) {
    return;
  }
  if (hasMatchingInFlightRequest(container, signature)) {
    return;
  }

  cancelInFlightRequest(container.id);
  lifecycle.destroyChart(container.id);
  renderedChartStates.delete(container.id);

  const currencyCode = normalizeDataValue(container.dataset.currencyCode);
  const url = `/companies/valuation-multiples-chart/${companyId}/all_history/`;
  const requestId = ++nextRequestId;
  const controller = new AbortController();
  inFlightRequests.set(container.id, { requestId, container, controller, signature });

  fetch(url, { signal: controller.signal })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      return response.json() as Promise<ValuationChartResponse>;
    })
    .then((response) => {
      if (!isLatestRequest(container, requestId)) return;

      const trailingData = transformData(response.data, "pe_last_twelve_months");
      const colors = getThemeColors();

      // Parse exit PE scenario values from data attributes
      const exitPeHigh = parseDataAttr(container, "exitPeHigh");
      const exitPeBase = parseDataAttr(container, "exitPeBase");
      const exitPeLow = parseDataAttr(container, "exitPeLow");
      const currentPe = parseDataAttr(container, "currentPe");
      const median = computeMedian(trailingData);

      // --- Build plotBands (exit PE range) ---
      const plotBands: Highcharts.YAxisPlotBandsOptions[] = [];
      if (exitPeHigh != null && exitPeLow != null) {
        plotBands.push({
          from: exitPeLow,
          to: exitPeHigh,
          color: colors.bandFill,
          label: {
            text: "Exit PE Range",
            align: "left",
            x: 4,
            style: {
              color: colors.bandLabel,
              fontSize: "9px",
              fontWeight: "500",
            },
          },
          zIndex: 1,
        });
      }

      // --- Build plotLines ---
      const plotLines: Highcharts.YAxisPlotLinesOptions[] = [];

      if (exitPeBase != null) {
        plotLines.push({
          value: exitPeBase,
          color: colors.exitBase,
          dashStyle: "Solid",
          width: 2.5,
          zIndex: 5,
          label: {
            text: `Exit Base: ${exitPeBase.toFixed(1)}x`,
            align: "right",
            x: -4,
            style: { color: colors.exitBase, fontSize: "10px", fontWeight: "600" },
          },
        });
      }

      if (currentPe != null) {
        plotLines.push({
          value: currentPe,
          color: colors.currentPe,
          dashStyle: "Solid",
          width: 2,
          zIndex: 4,
          label: {
            text: `Current: ${currentPe.toFixed(1)}x`,
            align: "right",
            x: -4,
            style: { color: colors.currentPe, fontSize: "10px", fontWeight: "600" },
          },
        });
      }

      if (median != null) {
        plotLines.push({
          value: median,
          color: colors.median,
          dashStyle: "ShortDash",
          width: 1,
          zIndex: 3,
          label: {
            text: `Median: ${median.toFixed(1)}x`,
            align: "right",
            x: -4,
            style: { color: colors.median, fontSize: "10px", fontWeight: "500" },
          },
        });
      }

      const subtitle = currencyCode ? `Currency: ${currencyCode}` : undefined;

      // A newer render may have created a chart while this request was in flight.
      lifecycle.destroyChart(container.id);

      const chart = Highcharts.stockChart(container.id, {
        chart: {
          zooming: { type: "x" },
        },
        exporting: CHART_EXPORTING_CONFIG,
        credits: { enabled: false },
        legend: { enabled: false },
        navigator: { enabled: false },
        scrollbar: { enabled: false },
        rangeSelector: {
          buttons: RANGE_SELECTOR_BUTTONS,
          selected: RANGE_SELECTOR_BUTTONS.length - 1,
          inputEnabled: false,
        },
        title: { text: undefined },
        subtitle: subtitle
          ? {
              text: subtitle,
              style: { fontSize: "10px" },
            }
          : undefined,
        xAxis: {
          type: "datetime",
        },
        yAxis: {
          title: { text: "P/E Ratio" },
          plotBands,
          plotLines,
        },
        tooltip: {
          valueDecimals: DEFAULT_VALUE_DECIMALS,
        },
        series: [
          {
            type: "area",
            name: "Trailing PE",
            data: trailingData,
            lineWidth: 1.5,
            color: colors.areaLine,
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [
                [0, colors.areaFillFrom],
                [1, "rgba(0,0,0,0)"],
              ],
            },
            threshold: null,
            tooltip: { valueDecimals: DEFAULT_VALUE_DECIMALS },
          },
        ],
      });

      if (!isLatestRequest(container, requestId)) {
        chart.destroy();
        return;
      }
      lifecycle.instances.set(container.id, chart);
      renderedChartStates.set(container.id, { container, signature });
    })
    .catch((error: unknown) => {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (!isLatestRequest(container, requestId)) return;
      renderedChartStates.delete(container.id);
      console.error("Error fetching PE data for Exit PE context chart:", error);
      if (document.getElementById(container.id) === container) {
        showChartError(container, "Failed to load chart");
      }
    })
    .finally(() => {
      if (isLatestRequest(container, requestId)) {
        inFlightRequests.delete(container.id);
      }
    });
}

const lifecycle = createChartLifecycle(".exit-pe-context-chart", renderChartForContainer);

/**
 * Initializes all Exit PE context charts on the page.
 *
 * Finds all elements with the .exit-pe-context-chart class and renders
 * charts based on their data attributes.
 */
export function initializeExitPeContextCharts(): void {
  const chartContainers = document.querySelectorAll<HTMLElement>(".exit-pe-context-chart");
  const hasChartContainers = chartContainers.length > 0;
  const activeIds = new Set<string>();

  chartContainers.forEach((container) => {
    if (!container.id) return;
    activeIds.add(container.id);
    renderChartForContainer(container);
  });

  // Clean up stale chart instances
  for (const containerId of lifecycle.instances.keys()) {
    const domContainer = document.getElementById(containerId);
    const renderedState = renderedChartStates.get(containerId);
    const replacedContainer = !!renderedState && renderedState.container !== domContainer;
    if (!activeIds.has(containerId) || !domContainer || replacedContainer) {
      cancelInFlightRequest(containerId);
      lifecycle.destroyChart(containerId);
      renderedChartStates.delete(containerId);
    }
  }

  // Clean up stale in-flight requests
  for (const [containerId, inFlight] of inFlightRequests.entries()) {
    const domContainer = document.getElementById(containerId);
    const replacedContainer = domContainer !== inFlight.container;
    if (!activeIds.has(containerId) || !domContainer || replacedContainer) {
      cancelInFlightRequest(containerId);
    }
  }

  // Clean up cached render states for removed/replaced containers
  for (const [containerId, renderedState] of renderedChartStates.entries()) {
    const domContainer = document.getElementById(containerId);
    if (!activeIds.has(containerId) || domContainer !== renderedState.container) {
      renderedChartStates.delete(containerId);
    }
  }

  // Keep theme observer tied to DOM containers, not chart instance timing.
  // This chart renders asynchronously after fetch() resolves.
  if (!hasChartContainers) {
    for (const containerId of inFlightRequests.keys()) {
      cancelInFlightRequest(containerId);
    }
    if (themeObserver) {
      themeObserver.disconnect();
      themeObserver = null;
    }
    return;
  }

  if (!themeObserver) {
    themeObserver = setupThemeObserver(() => {
      initializeExitPeContextCharts();
    });
  }
}

/**
 * Registers DOM listeners to auto-initialize Exit PE context charts.
 */
export function setupExitPeContextChartAutoInit(): void {
  lifecycle.setupAutoInit(initializeExitPeContextCharts);
}
